import * as React from 'react';

import { MultiSelectFilter } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useAtom, useAtomValue } from 'jotai';

import {
  facilitiesQueryAtom,
  FacilityRecord,
} from '../../../_graphql/queryFacilities';
import { $columnSelectedFacilityIds } from '../../../_state/loadsList.state';
import { useTranslations } from '../../../i18n/useTranslations';

interface FacilityColumnFilterProps {
  onClose?: () => void;
}

export const FacilityColumnFilter: React.FC<FacilityColumnFilterProps> = ({
  onClose,
}) => {
  const t = useTranslations();
  const [selectedFacilityIdsSet, setSelectedFacilityIdsSet] = useAtom(
    $columnSelectedFacilityIds,
  );
  const selectedFacilityIds = Array.from(selectedFacilityIdsSet);
  const [searchTerm, setSearchTerm] = React.useState<string | undefined>(
    undefined,
  );

  // Convert string array to Set for MultiSelectFilter
  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedFacilityIds),
    [selectedFacilityIds],
  );

  // Create atom for facilities query
  const facilitiesAtom = React.useMemo(
    () =>
      facilitiesQueryAtom(() => ({
        variables: {
          searchTerm,
          limit: 20,
        },
      })),
    [searchTerm],
  );

  // Fetch facilities matching the search term
  const {
    data: facilitiesData,
    isFetching: isFetchingFacilities,
    isLoading: isLoadingFacilities,
  } = useAtomValue(facilitiesAtom);

  const facilities = facilitiesData?.facilities ?? [];
  // Derive selected facilities from the existing facilities array
  const selectedFacilities = facilities.filter((facility) =>
    selectedFacilityIds.includes(facility.id),
  );

  // Handle search with debouncing via MultiSelectFilter
  const handleSearch = React.useCallback((query: string) => {
    setSearchTerm(query);
  }, []);

  // Handle selection changes
  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedFacilityIdsSet(new Set(Array.from(keys) as string[]));
    },
    [setSelectedFacilityIdsSet],
  );

  // Handle reset
  const handleReset = React.useCallback(() => {
    setSelectedFacilityIdsSet(new Set());
  }, [setSelectedFacilityIdsSet]);

  // Custom item renderer for facilities
  const getLabel = React.useCallback(
    (item: FacilityRecord, isSelected: boolean) => (
      <div className="flex w-full items-center gap-2">
        <div
          className={cn(
            'w-0 grow truncate text-sm',
            isSelected ? 'text-neutral' : 'text-neutral/60',
          )}
        >
          {item.facilityName}
        </div>
        {item.address?.city && item.address?.state && (
          <span className="rounded bg-neutral/10 px-1.5 py-0.5 text-xs text-neutral/60">
            {item.address.city}, {item.address.state}
          </span>
        )}
      </div>
    ),
    [],
  );

  return (
    <MultiSelectFilter
      className="@FacilityColumnFilter min-w-[350px]"
      entries={facilities}
      selectedEntries={selectedFacilities}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      getEntryKey={(facility) => facility.id}
      getEntryLabel={getLabel}
      onSearch={handleSearch}
      onDismiss={onClose}
      onReset={handleReset}
      searchPlaceholder={t.filters.searchFacilities}
      isLoading={isLoadingFacilities}
      isFetching={isFetchingFacilities}
    />
  );
};
